import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { SMS_ICON } from '../../../../app.component';
import { AiAgentResponse } from '../../../../models/responses/ai-agent-response';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AiConfigurationPageEventsService } from '../../../../events/ai-configuration-page-events.service';
import { SmsSettingsResponse } from '../../../../models/responses/sms-settings-response';
import { SmsMessageChoice } from '../../../../models/responses/sms-message-choice';
import { SmsSettingsService } from '../../../../services/sms-settings.service';
import { MinutesTimeUnitMappingComponent } from '../../../minutes-time-unit-mapping/minutes-time-unit-mapping.component';

@Component({
  selector: 'app-sms-setting-dialog',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    NgFor,
    MatIconModule,
    MatDialogModule,
    AsyncPipe,
    MatSelectModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MinutesTimeUnitMappingComponent,
    NgClass,
    NgIf
  ],
  templateUrl: './sms-setting-dialog.component.html',
  styleUrl: './sms-setting-dialog.component.scss'
})
export class SmsSettingDialogComponent {
  smsIcon: string = SMS_ICON;
  smsSettingsForm!: FormGroup;
  confirmationDelay: number
  isLoading = false;
  appMinuteTimeUnitMappingLabel = "Time to wait before sending information if the user does not confirm via text.";
  constructor(
    private formBuilder: FormBuilder,
    private smsMessageService: SmsSettingsService,
    public dialogRef: MatDialogRef<SmsSettingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { aiAgent: AiAgentResponse, smsSettings: SmsSettingsResponse },
    private snackBar: MatSnackBar,
    private aiConfigurationPageEventService: AiConfigurationPageEventsService
  ) {
    this.confirmationDelay = this.data.smsSettings?.confirmationDelay || 0;
  }

  ngOnInit() {
    this.initForm();
  }

  onMinutesValueChange(valueChanges: number) {
    this.confirmationDelay = valueChanges;
  }

  initForm() {
    this.smsSettingsForm = this.formBuilder.group({
      smsContent: [this.data.smsSettings ? this.data.smsSettings.content : ''],
      activeCondition: [this.data.smsSettings ? this.data.smsSettings.activeCondition : undefined],
      sendToCaller: [this.data.smsSettings ? this.data.smsSettings.sendToCaller : false],
      active: [this.data.smsSettings ? this.data.smsSettings.active : false],
      confirmUserInfo: [this.data.smsSettings ? this.data.smsSettings.confirmUserInfo : false],
      forceUserInfoPush: [this.data.smsSettings ? this.data.smsSettings.forceUserInfoPush : false],
      messageChoices: this.formBuilder.array([])
    });

    if (this.data.smsSettings?.messageChoices) {
      this.initMessageChoiceField();
    }
  }

  get messageChoices(): FormArray<FormGroup> {
    return this.smsSettingsForm.controls['messageChoices'] as FormArray<FormGroup>;
  }



  onSubmit() {
    if (this.smsSettingsForm.valid) {
      this.isLoading = true;

      const formValues = this.smsSettingsForm.value;

      if (this.data.smsSettings) {
        this.updateSmsSettings(formValues);
      } else {
        this.createSmsSettings(formValues)
      }
    }
  }


  onRemoveField(index: number) {
    this.messageChoices.removeAt(index);
  }
  onAddField() {
    this.messageChoices.push(this.formBuilder.group({
      id: [undefined],
      condition: ['', Validators.required],
      content: ['', Validators.required]
    }));
  }

  initMessageChoiceField() {
    this.data.aiAgent.smsSettings!.messageChoices.forEach(x => {
      this.messageChoices.push(this.formBuilder.group({
        id: [x.id, Validators.required],
        condition: [x.condition, Validators.required],
        content: [x.content, Validators.required]
      }))
    })
  }

  private createSmsSettings(formValues: any) {
    const smsSettings: SmsSettingsResponse = {
      sendToCaller: formValues['sendToCaller'],
      activeCondition:formValues['activeCondition'],
      content: formValues["smsContent"],
      active: formValues['active'],
      confirmUserInfo: formValues['confirmUserInfo'],
      confirmationDelay: this.confirmationDelay,
      forceUserInfoPush: formValues['forceUserInfoPush'],
      aiAgentId: this.data.aiAgent.id,
      messageChoices: this.getSmsMessageChoices()
    }

    this.smsMessageService.createSmsSettings(smsSettings).subscribe({
      next: (smsSetting) => {
        this.isLoading = false;
        this.aiConfigurationPageEventService.onSmsSettingsAddedEvent.emit(smsSetting);
        this.dialogRef.close();
      },
      error: () => {
        alert("server error please contact support");
        this.isLoading = false;
      }
    })
  }

  private updateSmsSettings(formValues: any) {
    const smsSettings: SmsSettingsResponse = {
      sendToCaller: formValues['sendToCaller'],
      content: formValues["smsContent"],
      active: formValues['active'],
      activeCondition: formValues['activeCondition'],
      confirmUserInfo: formValues['confirmUserInfo'],
      confirmationDelay: this.confirmationDelay,
      forceUserInfoPush: formValues['forceUserInfoPush'],
      id: this.data.smsSettings.id,
      aiAgentId: this.data.aiAgent.id,
      messageChoices: this.getSmsMessageChoices()
    }

    this.smsMessageService.updateSmsSettings(smsSettings).subscribe({
      next: (smsSetting) => {
        this.isLoading = false;
        this.aiConfigurationPageEventService.onSmsSettingsAddedEvent.emit(smsSetting);
        this.dialogRef.close();
      },
      error: () => {
        alert("server error please contact support");
        this.isLoading = false;
      }
    })
  }

  private getSmsMessageChoices() {
    const values: SmsMessageChoice[] = [];

    for (const messageChoiceForm of this.messageChoices.controls) {
      const messageChoiceFormValue = messageChoiceForm.value;
      const itemId = parseInt(messageChoiceFormValue["id"]);
      if (this.data.smsSettings) {
        values.push({
          condition: messageChoiceFormValue['condition'],
          content: messageChoiceFormValue['content'],
          id: isNaN(itemId) ? undefined : itemId,
          smsSettingsId: this.data.smsSettings.id
        });
      } else {
        values.push({
          condition: messageChoiceFormValue['condition'],
          content: messageChoiceFormValue['content'],
          id: isNaN(itemId) ? undefined : itemId,
        });
      }
    }
    return values
  }
}
